import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useAdminContext } from "@/hooks/useAdminContext";
import { supabase } from "@/integrations/supabase/client";
import { MainLayout } from "@/components/layout/MainLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { LeaderboardBlock } from "@/components/shared/LeaderboardBlock";
import { getScoreColor } from "@/lib/scoreUtils";
import { toast } from "sonner";
import {
  Star,
  Copy,
  ExternalLink,
  FileText,
  Flame,
  CalendarDays,
} from "lucide-react";
import { PromptTypesPieChart } from "@/components/PromptTypesPieChart";
import { TopPromptsRanked } from "@/components/exec/TopPromptsRanked";
import { RemixButton } from "@/components/feed/RemixButton";
import { useToggleFavorite, useCheckFavorited } from "@/hooks/useFavorites";
import { cn } from "@/lib/utils";

interface PromptOfDay {
  score_id: string;
  submission_id: string;
  score_total: number;
  detected_type: string;
  best_prompt: string | null;
  user_name: string;
  user_initials: string;
  created_at: string;
  evaluation_markdown?: string;
  favorite_count: number;
  tags: string[];
}

interface PromptTypeData {
  prompt_type: string;
  count: number;
}

interface OrgMomentumStats {
  total_prompts: number;
  daily_prompt_streak: number;
  weekly_prompt_streak: number;
  prompts_this_week: number;
}

const CATEGORY_COLORS: Record<string, string> = {
  'Planning': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  'Strategy': 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  'Research': 'bg-green-500/20 text-green-300 border-green-500/30',
  'Drafting': 'bg-orange-500/20 text-orange-300 border-orange-500/30',
  'Analysis': 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
  'Creative': 'bg-pink-500/20 text-pink-300 border-pink-500/30',
  'Brainstorming': 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
  'Summarizing': 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',
  'Unknown': 'bg-muted text-muted-foreground border-border',
};

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function getPromptSummary(pod: PromptOfDay): string {
  if (pod.evaluation_markdown) {
    try {
      const parsed = JSON.parse(pod.evaluation_markdown);
      if (parsed.prompt_summary) return parsed.prompt_summary;
    } catch {}
  }
  return 'Provides structured guidance for your task';
}

export default function Organization() {
  const { user, membership } = useAuth();
  const { isImpersonating, impersonatedOrgId, impersonatedOrgName } = useAdminContext();
  const navigate = useNavigate();
  
  const effectiveOrgId = isImpersonating ? impersonatedOrgId : membership?.org_id;
  const effectiveOrgName = isImpersonating ? impersonatedOrgName : membership?.org_name;
  
  const [loading, setLoading] = useState(true);
  const [promptOfDay, setPromptOfDay] = useState<PromptOfDay | null>(null);
  const [promptTypes, setPromptTypes] = useState<PromptTypeData[]>([]);
  const [orgMomentum, setOrgMomentum] = useState<OrgMomentumStats | null>(null);
  

  // Favorite state for Prompt of the Day
  const toggleFavoriteMutation = useToggleFavorite();
  const { data: isPodFavorited, isLoading: isPodFavLoading } = useCheckFavorited(
    user?.id, 
    promptOfDay?.score_id || ''
  );

  useEffect(() => {
    if (user && effectiveOrgId) {
      fetchAllData();
    }
  }, [user, effectiveOrgId]);

  const fetchAllData = async () => {
    if (!effectiveOrgId) return;
    setLoading(true);

    try {
      const [promptOfDayRes, typesRes, momentumRes] = await Promise.all([
        supabase.rpc('get_org_prompt_of_day', { _org_id: effectiveOrgId }),
        supabase.rpc('get_org_prompt_type_distribution', { _org_id: effectiveOrgId }),
        supabase.rpc('get_org_momentum_stats' as any, { _org_id: effectiveOrgId }) as any,
      ]);

      if (promptOfDayRes.data && promptOfDayRes.data.length > 0) {
        setPromptOfDay(promptOfDayRes.data[0] as PromptOfDay);
      }

      if (typesRes.data) setPromptTypes(typesRes.data as PromptTypeData[]);
      if (momentumRes.data && momentumRes.data.length > 0) {
        setOrgMomentum(momentumRes.data[0] as OrgMomentumStats);
      }

    } catch (error) {
      console.error("Error fetching organization data:", error);
      toast.error("Failed to load organization data");
    } finally {
      setLoading(false);
    }
  };

  const handlePodFavoriteToggle = () => {
    if (!user || !promptOfDay) {
      toast.error('Please sign in to save favorites');
      return;
    }
    toggleFavoriteMutation.mutate({ 
      userId: user.id, 
      scoreId: promptOfDay.score_id, 
      isFavorited: !!isPodFavorited 
    });
  };

  const handlePodCopy = async () => {
    if (!promptOfDay?.best_prompt) return;
    try {
      await navigator.clipboard.writeText(promptOfDay.best_prompt);
      toast.success('Prompt copied to clipboard');
      // Track copy action
      if (user) {
        await supabase.from('prompt_actions').insert({
          score_id: promptOfDay.score_id,
          user_id: user.id,
          action_type: 'copy',
        });
      }
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handlePodShare = async () => {
    if (!user || !promptOfDay) return;
    try {
      await supabase.from('prompt_actions').insert({
        score_id: promptOfDay.score_id,
        user_id: user.id,
        action_type: 'share_chatgpt',
      });
    } catch (err) {
      console.error('Failed to track share:', err);
    }
  };

  const handlePodRemix = () => {
    if (!promptOfDay?.best_prompt) return;
    navigate('/prompt', {
      state: {
        remixContext: {
          originalPrompt: promptOfDay.best_prompt,
          originalScoreId: promptOfDay.score_id,
        }
      }
    });
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-4 md:p-6 space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid md:grid-cols-2 gap-6">
            <Skeleton className="h-48" />
            <Skeleton className="h-48" />
          </div>
          <Skeleton className="h-64" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-4 md:p-6 space-y-6 overflow-x-hidden">
        {/* Header */}
        <div>
          <h1 className="text-2xl md:text-3xl font-display font-bold">{effectiveOrgName}</h1>
          <p className="text-muted-foreground mt-1">Organization-wide engagement and community</p>
        </div>

        {/* Row 1: Prompt of the Day + Leaderboards */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Prompt of the Day */}
          <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20 min-w-0">
            <CardHeader className="pb-2">
              <CardDescription>Highest-scoring prompt in the last 24 hours</CardDescription>
            </CardHeader>
            <CardContent>
              {promptOfDay ? (
                <div className="space-y-3">
                  {/* Row 1: "Prompt of the Day" label + timestamp */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Star className="h-4 w-4 text-yellow-500" />
                      <span className="text-[15px] font-medium text-foreground">Prompt of the Day</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatRelativeTime(promptOfDay.created_at)}
                    </span>
                  </div>

                  {/* Row 2: Score XX/100 + "Prompt Score" (left) + Category badge (right) */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-baseline gap-1.5">
                      <span className={`font-bold text-xl ${getScoreColor(promptOfDay.score_total)}`}>
                        {promptOfDay.score_total}
                      </span>
                      <span className="text-sm text-muted-foreground/60">/100</span>
                      <span className="text-xs text-muted-foreground ml-1">Prompt Score</span>
                    </div>
                    <Badge 
                      variant="outline" 
                      className={`text-xs border flex-shrink-0 rounded-md ${CATEGORY_COLORS[promptOfDay.detected_type] || CATEGORY_COLORS['Unknown']}`}
                    >
                      {promptOfDay.detected_type || 'Unknown'}
                    </Badge>
                  </div>

                  {/* Summary text (no box, high contrast) */}
                  <p className="text-sm text-foreground leading-relaxed">
                    {getPromptSummary(promptOfDay)}
                  </p>


                  {/* Actions row */}
                  <div className="flex items-center justify-between">
                    {/* Left: Star, Copy, ChatGPT */}
                    <div className="flex items-center gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className={cn("h-8 px-2 gap-1", isPodFavLoading && "opacity-50")}
                        onClick={handlePodFavoriteToggle}
                        disabled={toggleFavoriteMutation.isPending || isPodFavLoading}
                        title={isPodFavorited ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        <Star className={cn(
                          "h-4 w-4",
                          isPodFavorited 
                            ? "fill-yellow-500 text-yellow-500" 
                            : "text-muted-foreground"
                        )} />
                        {promptOfDay.favorite_count > 0 && (
                          <span className="text-xs text-muted-foreground">{promptOfDay.favorite_count}</span>
                        )}
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 p-0"
                        onClick={handlePodCopy}
                        title="Copy prompt"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-8 w-8 p-0"
                        asChild
                        title="Try in ChatGPT"
                        onClick={handlePodShare}
                      >
                        <a 
                          href={promptOfDay.best_prompt ? `https://chatgpt.com/?prompt=${encodeURIComponent(promptOfDay.best_prompt)}` : '#'} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    </div>

                    {/* Right: Remix button */}
                    <RemixButton onClick={handlePodRemix} />
                  </div>
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">No submissions in the last 24 hours. Be the first!</p>
              )}
            </CardContent>
          </Card>
          
          {/* Leaderboards */}
          {effectiveOrgId && <LeaderboardBlock orgId={effectiveOrgId} />}
        </div>

        {/* Row 2: Org Momentum + Top Prompts */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="border-border min-w-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Flame className="h-5 w-5 text-primary" />
                Org Momentum
              </CardTitle>
              <CardDescription>Shared prompting habits across your organization</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <FileText className="h-4 w-4" />
                    <p className="text-xs font-medium">Total Prompts</p>
                  </div>
                  <p className="mt-2 text-2xl font-bold tabular-nums">
                    {(orgMomentum?.total_prompts || 0).toLocaleString()}
                  </p>
                </div>
                <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <CalendarDays className="h-4 w-4" />
                    <p className="text-xs font-medium">This Week</p>
                  </div>
                  <p className="mt-2 text-2xl font-bold tabular-nums">
                    {orgMomentum?.prompts_this_week || 0}
                  </p>
                </div>
                <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
                  <p className="text-xs font-medium text-muted-foreground">New Prompt Streak</p>
                  <p className="mt-2 text-2xl font-bold tabular-nums">
                    {orgMomentum?.daily_prompt_streak || 0}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">days in a row</p>
                </div>
                <div className="rounded-lg border border-border/70 bg-muted/20 p-3">
                  <p className="text-xs font-medium text-muted-foreground">Weekly Streak</p>
                  <p className="mt-2 text-2xl font-bold tabular-nums">
                    {orgMomentum?.weekly_prompt_streak || 0}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">3+ prompt weeks</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {effectiveOrgId && <TopPromptsRanked orgId={effectiveOrgId} maxHeight="h-[380px]" compactHeader />}
        </div>

        {/* Row 3: Prompt Types */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="border-border min-w-0">
            <CardHeader>
              <CardTitle>Prompt Types</CardTitle>
              <CardDescription>Distribution across your organization</CardDescription>
            </CardHeader>
            <CardContent>
              <PromptTypesPieChart data={promptTypes} height={220} />
            </CardContent>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
}
